'use client'

import { useState, memo } from 'react'

import { Pencil } from 'lucide-react'

import { TransactionValue } from '~/components/TransactionValue'
import { Button } from '~/components/component-library'
import { AddressPopover } from '~/components/component-library/AddressDisplay/AddressPopover'
import { Pill } from '~/components/component-library/Pill'
import { Timestamp } from '~/components/component-library/Timestamp'
import { TransactionTitle } from '~/components/component-library/TransactionTitle'
import { TransferIcon } from '~/components/smart-accounts/InboundTransactionDialog'
import { TransactionIcon } from '~/components/smart-accounts/TransactionIcon'
import { UpdateTransactionMemoDialog } from '~/components/smart-accounts/UpdateTransactionMemoDialog'
import { TransactionFeedActions } from '~/components/smart-accounts/transactions/TransactionFeedItem/TransactionFeedActions'
import { TransactionFeedItemShell } from '~/components/smart-accounts/transactions/TransactionFeedItem/TransactionFeedItemShell'
import { useAuth } from '~/context/AuthProvider'
import { useTransactionProperties } from '~/hooks/useTransactions'
import {
  OrgAssetTransfer,
  OrgSmartAccount,
  OrgTransaction,
  TransactionStatus,
} from '~/types'
import { useIsMobile } from '~/utils/devices'

export const TransactionFeedItem = memo(function TransactionFeedItem({
  transaction,
  onClick,
  smartAccountId,
  smartAccount,
}: {
  transaction: OrgTransaction | OrgAssetTransfer
  onClick: (transaction: OrgTransaction | OrgAssetTransfer) => void
  smartAccountId?: string
  smartAccount?: OrgSmartAccount
}) {
  const orgTransaction = transaction.isSplitsTransaction
    ? transaction
    : undefined
  const assetTransfer = transaction.isSplitsTransaction
    ? undefined
    : transaction

  const { isConfirmed } = useTransactionProperties(orgTransaction)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const isFailed = orgTransaction?.status === TransactionStatus.Failed

  // Only transactions can be "active", since transfers are already onchain
  const showTransactionValue = !orgTransaction || isConfirmed

  return (
    <TransactionFeedItemShell
      onClick={() => onClick(transaction)}
      isHighlighted={isDropdownOpen}
      icon={
        orgTransaction ? (
          <TransactionIcon
            transaction={orgTransaction}
            networkLogo={!orgTransaction.isMerkleTransaction}
          />
        ) : (
          <TransferIcon transfer={assetTransfer!} networkLogo />
        )
      }
      title={
        <TransactionTitle
          transaction={transaction}
          textWrap={false}
          className="h-6 min-w-0 leading-6"
          showFeatures={transaction.isSplitsTransaction}
        />
      }
      subtext={
        <TransactionFeedSubtext
          transaction={orgTransaction}
          transfer={assetTransfer}
          smartAccountId={smartAccountId}
          smartAccount={smartAccount}
        />
      }
      trailing={
        isFailed ? (
          <div className="flex flex-shrink-0 items-center self-center">
            <Pill variant="danger" label="Failed" size="small" />
          </div>
        ) : (
          <div className="flex-shrink-0">
            {showTransactionValue && (
              <TransactionValue transaction={transaction} layout="two-line" />
            )}
          </div>
        )
      }
      actions={
        <TransactionFeedActions
          transaction={transaction}
          isDropdownOpen={isDropdownOpen}
          setIsDropdownOpen={setIsDropdownOpen}
        />
      }
    />
  )
})

const TransactionFeedSubtext = ({
  transaction,
  transfer,
  smartAccountId,
  smartAccount,
}: {
  transaction: OrgTransaction | undefined
  transfer: OrgAssetTransfer | undefined
  smartAccountId?: string
  smartAccount: OrgSmartAccount | undefined
}) => {
  if (!transaction && !transfer) {
    throw new Error('Transaction or transfer is required')
  }
  if (transaction && transfer) {
    throw new Error('Only one of transaction or transfer is allowed')
  }

  const { setGlobalModalState } = useAuth()

  const memo = transfer?.memo || transaction?.memo
  const timestamp =
    transaction?.transactionTime ??
    transaction?.createdAt ??
    transfer!.transactionTime

  return (
    <div className="flex h-4 flex-row items-start space-x-0.5 text-sm leading-4 text-secondary md:items-center">
      {!smartAccountId && smartAccount && (
        <>
          <div className="flex-shrink-0">
            <AddressPopover address={smartAccount.address} align="start">
              <div className="max-w-32 truncate whitespace-nowrap">
                {smartAccount.name}
              </div>
            </AddressPopover>
          </div>
          <div>&sdot;</div>
        </>
      )}
      <div className="flex-shrink-0">
        <div className="line-clamp-1">
          <Timestamp timestamp={timestamp} type="relative" abbreviate />
        </div>
      </div>
      {memo ? (
        <>
          <div>&sdot;</div>
          <button
            type="button"
            className="group/memo flex min-w-0 cursor-pointer items-center gap-1 text-left"
            onClick={(e) => {
              e.stopPropagation()
              setGlobalModalState({
                isOpen: true,
                content: (
                  <UpdateTransactionMemoDialog
                    setIsOpen={() =>
                      setGlobalModalState({ isOpen: false, content: null })
                    }
                    transaction={(transfer ?? transaction)!}
                  />
                ),
              })
            }}
          >
            <span className="line-clamp-1">{memo}</span>
            <Pencil className="hidden h-3 w-3 flex-shrink-0 group-hover/memo:inline" />
          </button>
        </>
      ) : (
        <AddMemoButton transaction={transfer ?? transaction} />
      )}
    </div>
  )
}

const AddMemoButton = ({
  transaction,
}: {
  transaction: OrgTransaction | OrgAssetTransfer | undefined
}) => {
  const { setGlobalModalState } = useAuth()
  const isMobile = useIsMobile()

  // must be a transaction or transfer to add a memo
  if (!transaction) return null

  // don't show on mobile, bc the button is almost always cut off
  if (isMobile) return null

  return (
    <>
      <div>&sdot;</div>
      <Button
        variant="secondaryAction"
        className="h-4"
        onClick={(e) => {
          e.stopPropagation()
          setGlobalModalState({
            isOpen: true,
            content: (
              <UpdateTransactionMemoDialog
                setIsOpen={() => {
                  setGlobalModalState({
                    isOpen: false,
                    content: null,
                  })
                }}
                transaction={transaction!}
              />
            ),
          })
        }}
      >
        Add memo
      </Button>
    </>
  )
}
